/**
 * admin-push-notification-api-serverless client.
 * POST /api-admin/v0/push/test via push-admin host by env toggle.
 */

import { requestPushAdmin } from './auth';

const PUSH_TEST_PATH = '/api-admin/v0/push/test';
const PUSH_PRODUCT_UPDATE_PATH = '/api-admin/v0/push/product-update';
const PUSH_PRODUCT_UPDATE_V2_PATH = '/api-admin/v0/push/product-update/v2';
const PUSH_PRODUCT_UPDATE_HISTORIES_PATH = '/api-admin/v0/push/product-update/histories';
const PUSH_RECOMMENDATION_PATH = '/api-admin/v0/push/recommendation';
const PUSH_RECOMMENDATION_V2_PATH = '/api-admin/v0/push/recommendation/v2';
const PUSH_RECOMMENDATION_HISTORIES_PATH = '/api-admin/v0/push/recommendation/histories';
const PUSH_DELIVERY_POLICIES_PATH = '/api-admin/v0/push/delivery-policies';
const PUSH_BROADCAST_POLICIES_PATH = '/api-admin/v0/push/broadcast-policies';
const PUSH_CATALOG_PATH = '/api-admin/v0/push/catalog';

export async function getPushCatalog(accessToken) {
  try {
    const res = await requestPushAdmin(PUSH_CATALOG_PATH, { method: 'GET' }, accessToken);
    const data = await parsePushAdminResponse(res, PUSH_CATALOG_PATH);
    if (!data || !Array.isArray(data.definitions) || typeof data.capabilities?.write_metadata !== 'boolean') {
      throw new Error('Invalid catalog response');
    }
    return { ...data, available: true };
  } catch (error) {
    if (error.status === 404 || error.status === 501 || error.code === 'CATALOG_DISABLED') {
      return { available: false, definitions: [], capabilities: { write_metadata: false } };
    }
    throw error;
  }
}

export async function writePushCatalog(method, notificationType, payload, accessToken) {
  if (!['POST', 'PATCH', 'DELETE'].includes(method)) throw new Error('Invalid catalog action');
  const path = method === 'POST' ? PUSH_CATALOG_PATH : `${PUSH_CATALOG_PATH}/${encodeURIComponent(notificationType)}`;
  const res = await requestPushAdmin(path, { method, body: JSON.stringify(payload) }, accessToken);
  return parsePushAdminResponse(res, path);
}

export async function getPushCatalogAudits(accessToken, notificationType = '') {
  const path = `${PUSH_CATALOG_PATH}?${new URLSearchParams({ include_audits: 'true', notification_type: notificationType })}`;
  const res = await requestPushAdmin(path, { method: 'GET' }, accessToken);
  return parsePushAdminResponse(res, path);
}

const BROADCAST_OPERATING_MODES = new Set(['OFF', 'OBSERVE', 'ENFORCE']);
const BROADCAST_READINESS_STATUSES = new Set(['READY', 'NOT_READY', 'UNKNOWN']);

async function parsePushAdminResponse(res, path) {
  const text = await res.text();
  let body = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    // non-JSON
  }

  if (process.env.NODE_ENV !== 'production') {
    console.log('[push-admin API]', path, res.status, body ?? text);
  }

  if (!res.ok) {
    const err = new Error(body?.msg || body?.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = body?.code;
    err.data = body;
    throw err;
  }

  return body?.data ?? body;
}

function pickField(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && value !== '') return value;
  }
  return undefined;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Infer campaign status when older histories API omits `status`.
 * v1 immediate sends and backfilled rows are treated as sent once delivery started.
 */
export function inferPushCampaignStatus(row) {
  const explicit = pickField(row, 'status', 'Status');
  if (explicit) return explicit;

  const attempted = toNumber(pickField(row, 'attemptedCount', 'AttemptedCount', 'attempted_count'));
  const completed = toNumber(pickField(row, 'completedCount', 'CompletedCount', 'completed_count'));
  const failed = toNumber(pickField(row, 'failedCount', 'FailedCount', 'failed_count'));

  if (attempted > 0) {
    if (completed + failed < attempted) return 'sending';
    return 'sent';
  }

  return '';
}

/** Merge v2 registration fields onto a histories list (GET may omit scheduleAt/status on older Lambdas). */
export function patchPushHistoryWithScheduleResult(data, scheduleResult) {
  if (!data || !scheduleResult?.campaignId) return data;
  const items = data.items ?? data.Items;
  if (!Array.isArray(items)) return data;

  const patched = items.map((row) => {
    const id = pickField(row, 'campaignId', 'CampaignId', 'campaign_id');
    if (id !== scheduleResult.campaignId) return normalizePushHistoryItem(row);
    return normalizePushHistoryItem({
      ...row,
      scheduleAt: scheduleResult.scheduleAt ?? row.scheduleAt,
      status: scheduleResult.status ?? row.status,
      deliveryMode: scheduleResult.deliveryMode ?? row.deliveryMode,
      localDate: scheduleResult.localDate ?? row.localDate,
      localTime: scheduleResult.localTime ?? row.localTime,
      includeInbox: scheduleResult.includeInbox ?? row.includeInbox,
      deliveryWindow: scheduleResult.deliveryWindow ?? row.deliveryWindow,
      contentLocales: scheduleResult.contentLocales ?? row.contentLocales,
    });
  });

  return { ...data, items: patched };
}

/** Normalize histories item (camelCase / PascalCase / snake_case). */
export function normalizePushHistoryItem(row) {
  if (!row || typeof row !== 'object') return row;

  const createdAt = pickField(row, 'createdAt', 'CreatedAt', 'created_at');
  const scheduleAt = pickField(row, 'scheduleAt', 'ScheduleAt', 'schedule_at');
  const status = inferPushCampaignStatus(row);

  return {
    ...row,
    campaignId: pickField(row, 'campaignId', 'CampaignId', 'campaign_id'),
    title: pickField(row, 'title', 'Title'),
    body: pickField(row, 'body', 'Body'),
    message: pickField(row, 'message', 'Message'),
    deliveryMode: pickField(row, 'deliveryMode', 'DeliveryMode', 'delivery_mode') ?? 'absolute',
    localDate: pickField(row, 'localDate', 'LocalDate', 'local_date') ?? '',
    localTime: pickField(row, 'localTime', 'LocalTime', 'local_time') ?? '',
    includeInbox: pickField(row, 'includeInbox', 'IncludeInbox', 'include_inbox') ?? true,
    deliveryWindow: pickField(row, 'deliveryWindow', 'DeliveryWindow', 'delivery_window') ?? null,
    targetDeviceCount: toNumber(
      pickField(row, 'targetDeviceCount', 'TargetDeviceCount', 'target_device_count'),
    ),
    scheduleAt: scheduleAt ?? '',
    status,
    attemptedCount: toNumber(
      pickField(row, 'attemptedCount', 'AttemptedCount', 'attempted_count'),
    ),
    completedCount: toNumber(
      pickField(row, 'completedCount', 'CompletedCount', 'completed_count'),
    ),
    failedCount: toNumber(
      pickField(row, 'failedCount', 'FailedCount', 'failed_count'),
    ),
    createdAt,
    updatedAt: pickField(row, 'updatedAt', 'UpdatedAt', 'updated_at'),
  };
}

function normalizePushProductUpdateHistories(data) {
  if (!data || typeof data !== 'object') return data;

  const items = data.items ?? data.Items ?? [];
  const page = data.page ?? data.Page ?? 1;
  const pageSize = data.pageSize ?? data.PageSize ?? data.page_size ?? 20;
  const totalCount = data.totalCount ?? data.TotalCount ?? data.total_count ?? 0;
  const totalPages = data.totalPages ?? data.TotalPages ?? data.total_pages
    ?? Math.max(1, Math.ceil(totalCount / pageSize));

  return {
    ...data,
    items: Array.isArray(items) ? items.map(normalizePushHistoryItem) : [],
    page,
    pageSize,
    totalCount,
    totalPages,
  };
}

/** POST /api-admin/v0/push/test */
export async function postPushTest(payload, accessToken) {
  const res = await requestPushAdmin(
    PUSH_TEST_PATH,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    accessToken,
  );
  return parsePushAdminResponse(res, PUSH_TEST_PATH);
}

/** POST /api-admin/v0/push/product-update */
export async function postPushProductUpdate(payload, accessToken) {
  const res = await requestPushAdmin(
    PUSH_PRODUCT_UPDATE_PATH,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    accessToken,
  );
  return parsePushAdminResponse(res, PUSH_PRODUCT_UPDATE_PATH);
}

/** POST /api-admin/v0/push/product-update/v2 — schedule campaign (immediate or future) */
export async function postPushProductUpdateV2(payload, accessToken) {
  const res = await requestPushAdmin(
    PUSH_PRODUCT_UPDATE_V2_PATH,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    accessToken,
  );
  return parsePushAdminResponse(res, PUSH_PRODUCT_UPDATE_V2_PATH);
}

/** DELETE /api-admin/v0/push/product-update/{campaignId} — cancel scheduled campaign */
export async function deletePushProductUpdate(campaignId, accessToken) {
  const path = `${PUSH_PRODUCT_UPDATE_PATH}/${encodeURIComponent(campaignId)}`;
  const res = await requestPushAdmin(path, { method: 'DELETE' }, accessToken);
  return parsePushAdminResponse(res, path);
}

/** GET /api-admin/v0/push/product-update/histories */
export async function getPushProductUpdateHistories(page, pageSize, accessToken) {
  const qs = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const path = `${PUSH_PRODUCT_UPDATE_HISTORIES_PATH}?${qs}`;
  const res = await requestPushAdmin(path, { method: 'GET' }, accessToken);
  const data = await parsePushAdminResponse(res, PUSH_PRODUCT_UPDATE_HISTORIES_PATH);
  return normalizePushProductUpdateHistories(data);
}

/** POST /api-admin/v0/push/recommendation */
export async function postPushRecommendation(payload, accessToken) {
  const res = await requestPushAdmin(
    PUSH_RECOMMENDATION_PATH,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    accessToken,
  );
  return parsePushAdminResponse(res, PUSH_RECOMMENDATION_PATH);
}

/** POST /api-admin/v0/push/recommendation/v2 — schedule campaign */
export async function postPushRecommendationV2(payload, accessToken) {
  const res = await requestPushAdmin(
    PUSH_RECOMMENDATION_V2_PATH,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    accessToken,
  );
  return parsePushAdminResponse(res, PUSH_RECOMMENDATION_V2_PATH);
}

/** DELETE /api-admin/v0/push/recommendation/{campaignId} */
export async function deletePushRecommendation(campaignId, accessToken) {
  const path = `${PUSH_RECOMMENDATION_PATH}/${encodeURIComponent(campaignId)}`;
  const res = await requestPushAdmin(path, { method: 'DELETE' }, accessToken);
  return parsePushAdminResponse(res, path);
}

/** GET /api-admin/v0/push/recommendation/histories */
export async function getPushRecommendationHistories(page, pageSize, accessToken) {
  const qs = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const path = `${PUSH_RECOMMENDATION_HISTORIES_PATH}?${qs}`;
  const res = await requestPushAdmin(path, { method: 'GET' }, accessToken);
  const data = await parsePushAdminResponse(res, PUSH_RECOMMENDATION_HISTORIES_PATH);
  return normalizePushProductUpdateHistories(data);
}

/** GET current environment's notification-type delivery policies. */
export async function listPushDeliveryPolicies(accessToken) {
  const res = await requestPushAdmin(PUSH_DELIVERY_POLICIES_PATH, { method: 'GET' }, accessToken);
  return parsePushAdminResponse(res, PUSH_DELIVERY_POLICIES_PATH);
}

/** GET append-only audit history for one notification type. */
export async function listPushDeliveryPolicyAudits(notificationType, accessToken, limit = 25) {
  const qs = new URLSearchParams({
    notification_type: notificationType,
    include_audits: 'true',
    limit: String(limit),
  });
  const path = `${PUSH_DELIVERY_POLICIES_PATH}?${qs}`;
  const res = await requestPushAdmin(path, { method: 'GET' }, accessToken);
  return parsePushAdminResponse(res, PUSH_DELIVERY_POLICIES_PATH);
}

/** PATCH an existing classified policy. New or unclassified types remain fail-closed. */
export async function updatePushDeliveryPolicy(notificationType, payload, accessToken) {
  const path = `${PUSH_DELIVERY_POLICIES_PATH}/${encodeURIComponent(notificationType)}`;
  const res = await requestPushAdmin(
    path,
    { method: 'PATCH', body: JSON.stringify(payload) },
    accessToken,
  );
  return parsePushAdminResponse(res, path);
}

/** GET the selected environment's broadcast delivery, global, slot, and usage snapshot. */
export async function getPushBroadcastPolicies(accessToken) {
  const res = await requestPushAdmin(PUSH_BROADCAST_POLICIES_PATH, { method: 'GET' }, accessToken);
  const snapshot = await parsePushAdminResponse(res, PUSH_BROADCAST_POLICIES_PATH);
  return normalizePushBroadcastSnapshot(snapshot);
}

/**
 * Keep the control tower compatible with the original dormant snapshot while
 * exposing the additive runtime status contract consistently to the page.
 */
export function normalizePushBroadcastSnapshot(snapshot = {}) {
  const global = snapshot.global_policy || {};
  const explicitMode = String(snapshot.operating_mode ?? global.operating_mode ?? '').toUpperCase();
  const operatingMode = BROADCAST_OPERATING_MODES.has(explicitMode)
    ? explicitMode
    : 'UNKNOWN';
  const rawUsage = snapshot.usage || {};
  const status = String(rawUsage.status || 'PENDING').toUpperCase();
  const legacyWindows = {
    hour: { provider_attempts: rawUsage.provider_attempts_current_hour ?? null },
    utc_day: {
      provider_attempts: rawUsage.provider_attempts_current_utc_day ?? null,
      suppressed: rawUsage.suppressed_current_utc_day ?? null,
    },
    month: { provider_attempts: rawUsage.provider_attempts_current_month ?? null },
  };
  const normalizedWindows = Array.isArray(rawUsage.windows)
    ? rawUsage.windows.reduce((result, row) => {
      const rawKey = String(row.window || row.period || row.key || '').toLowerCase();
      const key = rawKey === 'day' ? 'utc_day' : (rawKey === 'utc_month' ? 'month' : rawKey);
      if (['hour', 'utc_day', 'month'].includes(key)) result[key] = row;
      return result;
    }, {})
    : (rawUsage.windows || {});
  const readiness = snapshot.readiness || {};
  const rawReadinessStatus = String(readiness.status || 'UNKNOWN').toUpperCase();
  return {
    ...snapshot,
    operating_mode: operatingMode,
    readiness: {
      ...readiness,
      status: BROADCAST_READINESS_STATUSES.has(rawReadinessStatus)
        ? rawReadinessStatus : 'UNKNOWN',
      activation_allowed: readiness.activation_allowed === true,
      activation_supported: readiness.activation_supported === true,
      blockers: Array.isArray(readiness.blockers) ? readiness.blockers : [],
      checked_at: readiness.checked_at || null,
    },
    usage: {
      ...rawUsage,
      status,
      generated_at: rawUsage.generated_at || null,
      suppression_status: String(rawUsage.suppression_status || 'UNKNOWN').toUpperCase(),
      windows: { ...legacyWindows, ...normalizedWindows },
      by_notification_type: Array.isArray(rawUsage.by_notification_type)
        ? rawUsage.by_notification_type : [],
    },
  };
}

/** GET append-only broadcast policy audit history for one exact entity. */
export async function listPushBroadcastPolicyAudits(entityType, entityKey, accessToken, limit = 25) {
  const qs = new URLSearchParams({
    include_audits: 'true',
    entity_type: entityType,
    entity_key: entityKey,
    limit: String(limit),
  });
  const path = `${PUSH_BROADCAST_POLICIES_PATH}?${qs}`;
  const res = await requestPushAdmin(path, { method: 'GET' }, accessToken);
  return parsePushAdminResponse(res, PUSH_BROADCAST_POLICIES_PATH);
}

/** PATCH one exact notification type and audience variant. */
export async function updatePushBroadcastDeliveryPolicy(notificationType, audienceVariant, payload, accessToken) {
  const path = `${PUSH_BROADCAST_POLICIES_PATH}/delivery/${encodeURIComponent(notificationType)}/${encodeURIComponent(audienceVariant)}`;
  const res = await requestPushAdmin(path, { method: 'PATCH', body: JSON.stringify(payload) }, accessToken);
  return parsePushAdminResponse(res, path);
}

/** PATCH the singleton broadcast master policy. */
export async function updatePushBroadcastGlobalPolicy(payload, accessToken) {
  const path = `${PUSH_BROADCAST_POLICIES_PATH}/global`;
  const res = await requestPushAdmin(path, { method: 'PATCH', body: JSON.stringify(payload) }, accessToken);
  return parsePushAdminResponse(res, path);
}

/** PATCH or clear one numbered sender allowlist slot. */
export async function updatePushBroadcastSenderSlot(slotId, payload, accessToken) {
  const path = `${PUSH_BROADCAST_POLICIES_PATH}/sender-slots/${encodeURIComponent(slotId)}`;
  const res = await requestPushAdmin(path, { method: 'PATCH', body: JSON.stringify(payload) }, accessToken);
  return parsePushAdminResponse(res, path);
}
